"use client";

import * as React from "react";
import { Accordion as AccordionPrimitive } from "@base-ui/react/accordion";
import { ChevronDown, Plus } from "lucide-react";

import { cn } from "@/lib/utils";

interface AccordionProps extends React.HTMLAttributes<HTMLDivElement> {
  type?: "single" | "multiple";
  collapsible?: boolean;
  value?: string;
  defaultValue?: string | string[];
  onValueChange?: (value: string) => void;
}

const Accordion = React.forwardRef<HTMLDivElement, AccordionProps>(
  ({ type, collapsible, value, defaultValue, onValueChange, ...props }, ref) => (
    <div
      ref={ref}
      role="region"
      data-accordion-type={type}
      data-accordion-collapsible={collapsible}
      {...props}
    />
  )
);
Accordion.displayName = "Accordion";

const AccordionItem = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement> & { value?: string }
>(({ className, value, ...props }, ref) => (
  <div
    ref={ref}
    className={cn("", className)}
    role="region"
    data-accordion-value={value}
    {...props}
  />
));
AccordionItem.displayName = "AccordionItem";

const AccordionTrigger = React.forwardRef<
  HTMLButtonElement,
  React.ButtonHTMLAttributes<HTMLButtonElement> & {
    showPlusIcon?: boolean;
    editButton?: React.ReactNode;
  }
>(({ className, children, showPlusIcon = false, editButton, ...props }, ref) => (
  <div className="flex">
    <button
      ref={ref}
      className={cn(
        "flex flex-1 items-center justify-between py-4 px-6 font-semibold transition-all hover:bg-accent text-base",
        className
      )}
      role="button"
      {...props}
    >
      <div className="flex-grow text-left">{children}</div>
      <div className="flex items-center gap-3 ml-auto">
        {editButton && (
          <span
            className="text-blue-500 text-sm font-medium cursor-pointer hover:underline"
            onClick={(e) => {
              e.stopPropagation();
            }}
          >
            {editButton}
          </span>
        )}
        {showPlusIcon ? (
          <Plus
            className="h-5 w-5 shrink-0 text-muted-foreground transition-transform duration-200 data-[open]:rotate-45"
          />
        ) : (
          <ChevronDown
            className="h-5 w-5 shrink-0 text-muted-foreground transition-transform duration-200 data-[open]:rotate-180"
          />
        )}
      </div>
    </button>
  </div>
));
AccordionTrigger.displayName = "AccordionTrigger";

const AccordionContent = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, children, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(
      "overflow-hidden border-t border-border data-[closed]:animate-accordion-up data-[open]:animate-accordion-down",
      className
    )}
    role="region"
    {...props}
  >
    <div className="px-6 py-4">{children}</div>
  </div>
));
AccordionContent.displayName = "AccordionContent";

export { Accordion, AccordionItem, AccordionTrigger, AccordionContent };
